import { __rest } from "tslib";
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";
/**
 * @example
 *
 * ```jsx
 * import {
 *   Button,
 *   Dialog,
 *   DialogContent,
 *   DialogDescription,
 *   DialogFooter,
 *   DialogHeader,
 *   DialogTitle,
 *   DialogTrigger,
 *   Input,
 *   Label,
 * } from "@skve/components"
 *
 * function Demo() {
 *   return (
 *     <Dialog>
 *       <DialogTrigger asChild>
 *         <Button variant="outline">Edit Profile</Button>
 *       </DialogTrigger>
 *       <DialogContent className="sm:max-w-[425px]">
 *         <DialogHeader>
 *           <DialogTitle>Edit profile</DialogTitle>
 *           <DialogDescription>
 *             Make changes to your profile here. Click save when you're done.
 *           </DialogDescription>
 *         </DialogHeader>
 *         <div className="grid gap-4 py-4">
 *           <div className="grid grid-cols-4 items-center gap-4">
 *             <Label htmlFor="name" className="text-right">
 *               Name
 *             </Label>
 *             <Input
 *               id="name"
 *               defaultValue="Pedro Duarte"
 *               className="col-span-3"
 *             />
 *           </div>
 *           <div className="grid grid-cols-4 items-center gap-4">
 *             <Label htmlFor="username" className="text-right">
 *               Username
 *             </Label>
 *             <Input
 *               id="username"
 *               defaultValue="@peduarte"
 *               className="col-span-3"
 *             />
 *           </div>
 *         </div>
 *         <DialogFooter>
 *           <Button type="submit">Save changes</Button>
 *         </DialogFooter>
 *       </DialogContent>
 *     </Dialog>
 *   )
 * }
 * ```
 */
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogOverlay = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(DialogPrimitive.Overlay, Object.assign({ ref: ref, className: cn("fixed inset-0 z-[500] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", className) }, props)));
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DialogContent = React.forwardRef((_a, ref) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    return (React.createElement(DialogPortal, null,
        React.createElement(DialogOverlay, null),
        React.createElement(DialogPrimitive.Content, Object.assign({ ref: ref, className: cn("fixed left-[50%] top-[50%] z-[500] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg app-dialog", className) }, props),
            children,
            React.createElement(DialogPrimitive.Close, { className: "absolute right-3 top-3 rounded-full opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none bg-primary p-1 text-white" },
                React.createElement(X, { className: "h-3 w-3" }),
                React.createElement("span", { className: "sr-only" }, "Schlie\u00DFen")))));
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("div", Object.assign({ className: cn("flex flex-col space-y-1.5 text-center sm:text-left", className) }, props)));
};
DialogHeader.displayName = "DialogHeader";
const DialogFooter = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("div", Object.assign({ className: cn("flex flex-col-reverse sm:flex-row sm:justify-end gap-2", className) }, props)));
};
DialogFooter.displayName = "DialogFooter";
const DialogTitle = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(DialogPrimitive.Title, Object.assign({ ref: ref, className: cn("text-lg font-semibold leading-none tracking-tight", className) }, props)));
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(DialogPrimitive.Description, Object.assign({ ref: ref, className: cn("text-sm text-muted-foreground", className) }, props)));
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;
export { Dialog, DialogPortal, DialogOverlay, DialogClose, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription, };
//# sourceMappingURL=dialog.js.map